/**
 * @flow
 */

import React, { useCallback, useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import type { ModalDialog, Client } from '../../../type';

import { GET_CLIENTS_QUERY } from './ClientsList';
import { CLIENT_SUMMARY_QUERY } from './ClientsPage';

// Mutation that deletes specified client
const DELETE_CLIENT_QUERY = gql`
    mutation deleteClient($input: DeleteClientInput!) {
        deleteClient(input: $input) {
            clientId
        }
    }
`;

const updateSummary = (store, salon, gender) => {
    const data = store.readQuery({
        query: CLIENT_SUMMARY_QUERY,
        variables: {
            salon,
        },
    });

    if (
        data &&
        data.viewer &&
        data.viewer.salon &&
        data.viewer.salon.clients &&
        data.viewer.salon.clients.summary
    ) {
        const summary = data.viewer.salon.clients.summary;
        let males = summary.males || 0;
        let females = summary.females || 0;
        if (gender === 'MALE') {
            males = Math.max(0, males - 1);
        } else if (gender === 'FEMALE') {
            females = Math.max(0, females - 1);
        }

        store.writeQuery({
            query: CLIENT_SUMMARY_QUERY,
            variables: {
                salon,
            },
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        clients: {
                            ...data.viewer.salon.clients,
                            summary: {
                                ...summary,
                                total: Math.max(0, (summary.total || 0) - 1),
                                males,
                                females,
                            },
                        },
                    },
                },
            },
        });
    }
};

type DeleteClientDialogProps = {
    client: Client,
} & ModalDialog;

const DeleteClientDialog = ({
    client,
    trigger,
    open,
    onClose,
}: DeleteClientDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ clients }) =>
        clients.get('searchQuery'),
    );

    const [deleteClient, { loading }] = useMutation(DELETE_CLIENT_QUERY);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = useCallback(() => {
        setDeleteFailed(false);
        // No optimisticResponse: the row stays visible while the request is
        // in flight, so the modal can show an error if the server rejects it.
        // On success the cache update removes the row, naturally unmounting
        // this component — no need to call onClose() in .then().
        deleteClient({
            variables: {
                input: {
                    salonId: salon,
                    clientId: client.id,
                },
            },
            update: (store, { data: { deleteClient: result } }) => {
                const data = store.readQuery({
                    query: GET_CLIENTS_QUERY,
                    variables: {
                        salon,
                        search: searchQuery || undefined,
                        first: 20,
                    },
                });

                if (
                    data &&
                    data.viewer &&
                    data.viewer.salon &&
                    data.viewer.salon.clients &&
                    data.viewer.salon.clients.edges
                ) {
                    store.writeQuery({
                        query: GET_CLIENTS_QUERY,
                        variables: {
                            salon,
                            search: searchQuery || undefined,
                            first: 20,
                        },
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                salon: {
                                    ...data.viewer.salon,
                                    clients: {
                                        ...data.viewer.salon.clients,
                                        edges: data.viewer.salon.clients.edges.filter(
                                            edge =>
                                                edge.node.id !== client.id,
                                        ),
                                    },
                                },
                            },
                        },
                    });
                }

                updateSummary(store, salon, client.gender);
            },
        }).catch(() => {
            setDeleteFailed(true);
        });
    }, [deleteClient, salon, client, searchQuery]);

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.clients.DeleteClientDialog.delete"
                    defaultMessage="Delete Client"
                />
            </Header>
            <Modal.Content>
                <p>
                    <FormattedMessage
                        id="pages.clients.DeleteClientDialog.deleteText"
                        defaultMessage="Are you sure you want to delete {firstName} {lastName}?"
                        values={{
                            firstName: client.firstName,
                            lastName: client.lastName,
                        }}
                    />
                </p>
                {deleteFailed && (
                    <p>
                        <FormattedMessage
                            id="errors.defaultMessage"
                            defaultMessage="Something went wrong"
                        />
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.clients.DeleteClientDialog.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button onClick={onDelete} color="green" inverted loading={loading} disabled={loading}>
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.clients.DeleteClientDialog.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteClientDialog;
